import React from 'react';
import { Icon, parseRatio, sectorRadius, Wheel } from 'react-svg';
import './style.scss';
import type { CategoriesRendererComponent, CategoriesRendererProps } from './types';

const CategoriesWheel: CategoriesRendererComponent = ({
  categories = [],
  aspectRatioStr,
  iconPaddingStr,
  fontSizeStr,
  currentCategoryIndex = null,
  onOpenCategory = () => {},
  children,
}: CategoriesRendererProps) => {
  /**
   * Ratio of the block's width to its height (w / h)
   */
  const aspectRatio = parseRatio(aspectRatioStr) || 16 / 9;

  /**
   * Size of fonts.
   */
  const fontSize = parseFloat(fontSizeStr) || 2;

  /**
   * Relative dimensions of the svg's view box
   * The lesser dimension is always 100
   */
  const [viewW, viewH] = aspectRatio > 1 ? [aspectRatio * 100, 100] : [100, 100 / aspectRatio];

  /**
   * Whether there is an even number of categories
   * Helps prevent text clipping
   */
  const even = !!(categories.length & 1);

  /**
   * A conditional offset from the Y origin's usual location
   * Helps prevent text clipping
   */
  const originYOffset = even ? fontSize : fontSize / 2;

  /**
   * Position of the svg's interior point of origin.
   * Measured relative to the svg's top-left corner.
   */
  const [originX, originY] = [-(viewW / 2), -(viewH / 2) + originYOffset];

  /**
   * A conditional increment from the usual wheel diameter.
   * Helps prevent text clipping.
   */
  const wheelDiameterInc = -(even ? fontSize * 2 : fontSize);

  /**
   * Total diameter of the wheel.
   */
  const wheelDiameter = 100 + wheelDiameterInc;

  /**
   * Angle of a sector in the wheel.
   */
  const wheelSectorAngle = (Math.PI * 2) / (categories.length || 1);

  /**
   * Maximum possible diameter of an icon.
   */
  const iconDiameterMax = wheelDiameter * sectorRadius(wheelSectorAngle);

  /**
   * Ratio of the distance between icons to the size of one icon.
   */
  const iconPaddingRatio = (parseFloat(iconPaddingStr) || 0) / 100;

  /**
   * Diameter of one icon.
   * Assumes icon width = icon height.
   */
  const iconDiameter = iconDiameterMax - iconDiameterMax * iconPaddingRatio;

  /**
   * Offset of an icon's center along a wheel spoke.
   */
  const wheelDiameterInner = wheelDiameter - iconDiameter;

  return (
    <div className="categories-wheel">
      <svg viewBox={`${originX} ${originY} ${viewW} ${viewH}`}>
        <Icon
          className="svg-icon-center"
          {...categories[0]}
          href={
            categories[0].action === 'productPage' &&
            categories[0].products &&
            categories[0].productIds[0] &&
            categories[0].products[categories[0].productIds[0]] &&
            categories[0].products[categories[0].productIds[0]].detailPageUrl
              ? categories[0].products[categories[0].productIds[0]].detailPageUrl
              : null
          }
          onClick={categories[0].action === 'popup' ? () => onOpenCategory(0) : null}
          fontSize={fontSize}
          diameter={iconDiameter}
        />
        {categories.length && (
          <Wheel diameter={wheelDiameterInner}>
            {categories.slice(1).map(({ action = 'popup', ...props }, i) => (
              <Icon
                {...props}
                key={i}
                href={
                  action === 'productPage' &&
                  props.productIds &&
                  props.productIds[0] &&
                  props.products[props.productIds[0]] &&
                  props.products[props.productIds[0]].detailPageUrl
                    ? props.products[props.productIds[0]].detailPageUrl
                    : null
                }
                onClick={action === 'popup' ? () => onOpenCategory(i + 1) : null}
                diameter={iconDiameter}
                fontSize={fontSize}
              />
            ))}
          </Wheel>
        )}
      </svg>
      {currentCategoryIndex != null && children}
    </div>
  );
};

export default CategoriesWheel;
